// Preview-mode mock API client. Returns built-in sample data shaped to match the
// real xplan backend responses (see ./types), so the whole embedded UI can be
// rendered and interacted with WITHOUT a Shopify host/App Bridge or any backend.
//
// Only used when the app is opened in preview mode (see App.tsx). The real
// (App Bridge–authenticated) path is untouched.
//
// Compliance: sample copy is neutral product wording — no claims about ranking,
// traffic or revenue outcomes.

import type { ApiClient } from "./client";
import type {
  ApproveResult,
  AutomationOut,
  AutomationUpdateIn,
  ContentAsset,
  ContentAssetDetail,
  CursorPage,
  MeStore,
  Opportunity,
  PublishFunnel,
  RevenueDailyReport,
  RevenueDailyRow,
  StoreConfig,
  StoreConfigOut,
  StoreDetail,
} from "./types";

const STORE_ID = "11111111-1111-4111-8111-111111111111";
const NOW = Date.now();
const DAY = 86_400_000;

// ISO timestamp `daysAgo` days before now.
function iso(daysAgo: number): string {
  return new Date(NOW - daysAgo * DAY).toISOString();
}
// Date-only string (YYYY-MM-DD) `daysAgo` days before now.
function dateOnly(daysAgo: number): string {
  return new Date(NOW - daysAgo * DAY).toISOString().slice(0, 10);
}

// --- Mutable in-memory state so saves/approvals persist within the session ---

interface MutableState {
  seoConfig: StoreConfig;
  automation: { mode: AutomationUpdateIn["mode"]; paused: boolean };
}

const state: MutableState = {
  seoConfig: {
    seo_blog_id: "gid://shopify/Blog/78123456789",
    seo_blog_handle: "news",
    seo_frontend_base_url: "https://demo-store.example.com",
    gsc_property: "sc-domain:demo-store.example.com",
  },
  automation: { mode: "review_auto", paused: false },
};

// --- Store ---

const meStore: MeStore = {
  id: STORE_ID,
  platform: "shopify",
  shop_domain: "demo-store.myshopify.com",
  name: "Demo Store",
  primary_domain: "demo-store.example.com",
  status: "active",
  connections: [
    { provider: "shopify", status: "connected", connected_at: iso(42) },
    { provider: "google", status: "connected", connected_at: iso(30) },
  ],
};

// --- Opportunities ---

const opportunities: Opportunity[] = [
  {
    id: "opp-1",
    store_id: STORE_ID,
    cluster_id: "cluster-a",
    text: "best running shoes for flat feet",
    normalized: "best running shoes for flat feet",
    language: "en",
    intent: "commercial",
    source: "gsc",
    status: "scored",
    search_volume: 2400,
    opportunity_score: 87,
    created_at: iso(12),
  },
  {
    id: "opp-2",
    store_id: STORE_ID,
    cluster_id: "cluster-a",
    text: "how to clean white sneakers",
    normalized: "how to clean white sneakers",
    language: "en",
    intent: "informational",
    source: "gsc",
    status: "scored",
    search_volume: 5800,
    opportunity_score: 74,
    created_at: iso(11),
  },
  {
    id: "opp-3",
    store_id: STORE_ID,
    cluster_id: "cluster-b",
    text: "waterproof hiking boots review",
    normalized: "waterproof hiking boots review",
    language: "en",
    intent: "commercial",
    source: "gsc",
    status: "scored",
    search_volume: 1300,
    opportunity_score: 69,
    created_at: iso(10),
  },
  {
    id: "opp-4",
    store_id: STORE_ID,
    cluster_id: "cluster-b",
    text: "trail running vs road running shoes",
    normalized: "trail running vs road running shoes",
    language: "en",
    intent: "informational",
    source: "gsc",
    status: "scored",
    search_volume: 900,
    opportunity_score: 61,
    created_at: iso(9),
  },
  {
    id: "opp-5",
    store_id: STORE_ID,
    cluster_id: "cluster-c",
    text: "vegan leather boots for winter",
    normalized: "vegan leather boots for winter",
    language: "en",
    intent: "commercial",
    source: "gsc",
    status: "scored",
    search_volume: 720,
    opportunity_score: 55,
    created_at: iso(8),
  },
  {
    id: "opp-6",
    store_id: STORE_ID,
    cluster_id: "cluster-c",
    text: "how to break in leather boots",
    normalized: "how to break in leather boots",
    language: "en",
    intent: "informational",
    source: "gsc",
    status: "scored",
    search_volume: 3100,
    opportunity_score: 48,
    created_at: iso(7),
  },
  {
    id: "opp-7",
    store_id: STORE_ID,
    cluster_id: null,
    text: "kids school shoes size guide",
    normalized: "kids school shoes size guide",
    language: "en",
    intent: "informational",
    source: "seed",
    status: "scored",
    search_volume: 480,
    opportunity_score: 41,
    created_at: iso(6),
  },
];

// --- Content assets (list + detail) ---

const SAMPLE_BODY = `
<h2>Choosing running shoes when you have flat feet</h2>
<p>Flat feet (fallen arches) change how weight moves through your stride, so the
right pair of shoes focuses on stability and support rather than raw cushioning.
This guide walks through what to look for and how to test a pair before you buy.</p>
<h3>What to look for</h3>
<ul>
  <li><strong>Structured support</strong> through the midsole to limit inward roll.</li>
  <li><strong>A firm heel counter</strong> that keeps the rearfoot aligned.</li>
  <li><strong>A wider base</strong> for a stable platform on longer runs.</li>
</ul>
<h3>How to test the fit</h3>
<p>Try shoes at the end of the day when your feet are at their largest, and walk
or jog a short distance in-store. Your heel should feel locked in with room to
wiggle your toes.</p>
<p>Pair the right shoes with a gradual training plan and replace them once the
midsole starts to compress.</p>
`.trim();

interface MockContent {
  asset: ContentAsset;
  detail: ContentAssetDetail;
}

function makeContent(
  id: string,
  title: string,
  slug: string,
  status: string,
  createdDaysAgo: number,
  targets: ContentAssetDetail["targets"],
  excerpt: string,
): MockContent {
  const asset: ContentAsset = {
    id,
    store_id: STORE_ID,
    content_type: "blog_post",
    title,
    slug,
    language: "en",
    status,
    created_at: iso(createdDaysAgo),
  };
  const detail: ContentAssetDetail = {
    ...asset,
    latest_version: {
      id: `${id}-v1`,
      version: status === "published" ? 2 : 1,
      title,
      body: SAMPLE_BODY,
      excerpt,
      seo_title: `${title} | Demo Store`,
      seo_description: excerpt,
      content_hash: `hash-${id}`,
      generated_by: "rankvine-llm",
      created_at: iso(createdDaysAgo),
    },
    targets,
  };
  return { asset, detail };
}

const contents: MockContent[] = [
  makeContent(
    "content-1",
    "Best Running Shoes for Flat Feet: A Buyer's Guide",
    "best-running-shoes-flat-feet",
    "published",
    9,
    [
      {
        id: "tgt-1",
        platform_resource_type: "shopify_article",
        platform_resource_id: "gid://shopify/Article/55511122233",
        status: "verified",
        scheduled_at: iso(8),
        published_at: iso(8),
        attempts: 1,
      },
    ],
    "A practical guide to picking supportive running shoes when you have flat feet, with fit tips.",
  ),
  makeContent(
    "content-2",
    "How to Clean White Sneakers Without Damaging Them",
    "how-to-clean-white-sneakers",
    "published",
    7,
    [
      {
        id: "tgt-2",
        platform_resource_type: "shopify_article",
        platform_resource_id: "gid://shopify/Article/55511122234",
        status: "published",
        scheduled_at: iso(6),
        published_at: iso(6),
        attempts: 1,
      },
    ],
    "Step-by-step methods to clean white sneakers by material, plus what to avoid.",
  ),
  makeContent(
    "content-3",
    "Waterproof Hiking Boots: What Actually Matters",
    "waterproof-hiking-boots-review",
    "draft",
    3,
    [
      {
        id: "tgt-3",
        platform_resource_type: "shopify_article",
        platform_resource_id: null,
        status: "pending",
        scheduled_at: null,
        published_at: null,
        attempts: 0,
      },
    ],
    "How waterproofing, breathability and fit trade off in hiking boots, and how to choose.",
  ),
  makeContent(
    "content-4",
    "Trail Running vs Road Running Shoes: Which Do You Need?",
    "trail-vs-road-running-shoes",
    "draft",
    2,
    [
      {
        id: "tgt-4",
        platform_resource_type: "shopify_article",
        platform_resource_id: null,
        status: "pending",
        scheduled_at: null,
        published_at: null,
        attempts: 0,
      },
    ],
    "The real differences between trail and road shoes, and how to pick for your routes.",
  ),
  makeContent(
    "content-5",
    "How to Break In Leather Boots the Right Way",
    "how-to-break-in-leather-boots",
    "draft",
    1,
    [
      {
        id: "tgt-5",
        platform_resource_type: "shopify_article",
        platform_resource_id: null,
        status: "validated",
        scheduled_at: null,
        published_at: null,
        attempts: 0,
      },
    ],
    "Comfortable ways to break in new leather boots and avoid blisters.",
  ),
  makeContent(
    "content-6",
    "Kids' School Shoes Size Guide for Parents",
    "kids-school-shoes-size-guide",
    "draft",
    0,
    [
      {
        id: "tgt-6",
        platform_resource_type: "shopify_article",
        platform_resource_id: null,
        status: "pending",
        scheduled_at: null,
        published_at: null,
        attempts: 0,
      },
    ],
    "How to measure children's feet at home and choose the right school shoe size.",
  ),
];

// --- Revenue daily (14 days) ---

function buildRevenue(): RevenueDailyReport {
  const rows: RevenueDailyRow[] = [];
  for (let d = 13; d >= 0; d--) {
    const orders = 3 + ((d * 7) % 9);
    const gross = 120 + ((d * 53) % 380);
    rows.push({
      date: dateOnly(d),
      currency: "USD",
      orders_count: orders,
      leads_count: orders + ((d * 3) % 5),
      gross_revenue: gross.toFixed(2),
      // refunds_reliable=false → refunds read as zero, net equals gross.
      refunded_amount: "0.00",
      net_revenue: gross.toFixed(2),
    });
  }
  return {
    store_id: STORE_ID,
    model: "last_non_direct",
    channel: "organic_search",
    refunds_reliable: false,
    rows,
  };
}

// --- Publish funnel ---

const publishFunnel: PublishFunnel = {
  store_id: STORE_ID,
  funnel: {
    draft: 4,
    validated: 1,
    scheduled: 1,
    publishing: 0,
    published: 3,
    verified: 1,
    monitored: 2,
    failed: 0,
  },
  total: 12,
};

// Small artificial latency so loading states are demonstrable in preview.
function delay<T>(value: T, ms = 220): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

// Build a client that satisfies the real ApiClient surface, backed by mock data.
export function createMockApiClient(): ApiClient {
  const client: ApiClient = {
    // `request` isn't used by any page directly, but must exist to satisfy the
    // ApiClient type. In preview mode there is no network, so reject clearly.
    request: <T>() =>
      Promise.reject(new Error("Preview mode: raw request() is not available.")) as Promise<T>,

    getMyStore: () => delay(meStore),

    getStore: (): Promise<StoreDetail> =>
      delay({
        id: STORE_ID,
        platform: "shopify",
        shop_domain: "demo-store.myshopify.com",
        name: "Demo Store",
        primary_domain: "demo-store.example.com",
        timezone: "America/New_York",
        currency: "USD",
        language: "en",
        status: "active",
        created_at: iso(42),
        connections: [
          {
            provider: "shopify",
            status: "connected",
            external_account_id: "demo-store.myshopify.com",
            scopes: "read_content,write_content",
            api_version: "2024-10",
            connected_at: iso(42),
            disconnected_at: null,
          },
          {
            provider: "google",
            status: "connected",
            external_account_id: "sc-domain:demo-store.example.com",
            scopes: "webmasters.readonly",
            api_version: "v3",
            connected_at: iso(30),
            disconnected_at: null,
          },
        ],
        seo_blog_id: state.seoConfig.seo_blog_id,
        seo_blog_handle: state.seoConfig.seo_blog_handle,
        seo_frontend_base_url: state.seoConfig.seo_frontend_base_url,
        gsc_property: state.seoConfig.gsc_property,
        automation: {
          mode: state.automation.mode,
          is_paused: state.automation.paused,
          observation_until: null,
        },
      }),

    updateStoreConfig: (_storeId: string, body: Partial<StoreConfig>): Promise<StoreConfigOut> => {
      state.seoConfig = { ...state.seoConfig, ...body };
      return delay({ store_id: STORE_ID, ...state.seoConfig });
    },

    updateAutomation: (_storeId: string, body: AutomationUpdateIn): Promise<AutomationOut> => {
      if (body.mode !== undefined) state.automation.mode = body.mode;
      if (body.paused !== undefined) state.automation.paused = body.paused;
      return delay({
        mode: state.automation.mode ?? "review_auto",
        observation_until: null,
        is_paused: state.automation.paused,
      });
    },

    listOpportunities: (): Promise<CursorPage<Opportunity>> =>
      delay({ items: opportunities, next_cursor: null }),

    listContents: (): Promise<CursorPage<ContentAsset>> =>
      delay({ items: contents.map((c) => c.asset), next_cursor: null }),

    getContent: (id: string): Promise<ContentAssetDetail> => {
      const found = contents.find((c) => c.asset.id === id);
      return delay((found ?? contents[0]).detail);
    },

    approveContent: (id: string): Promise<ApproveResult> => {
      const found = contents.find((c) => c.asset.id === id);
      if (found && found.asset.status === "draft") {
        found.asset.status = "scheduled";
        found.detail.status = "scheduled";
      }
      return delay({
        content_asset_id: id,
        advanced: 1,
        approved: 1,
        publish_task_ids: [`task-${id}`],
        revived: 0,
        targets: found ? found.detail.targets : [],
      });
    },

    listTasks: () => delay({ items: [], next_cursor: null }),

    getPublishFunnel: (): Promise<PublishFunnel> => delay(publishFunnel),

    getRevenueDaily: (): Promise<RevenueDailyReport> => delay(buildRevenue()),
  };
  return client;
}
